import { useState } from "react";
import authFetch, { setAuthToken } from "../authFetch";
import PT from "../i18n/platformTranslations";
import { usePlatformThemeMode, themeColors } from "../platform/themeUtils";
import { buildDarkAccentCss, buildLightAccentCss } from "../utils/themeAccent";

/* ════════════════════════════════════════
   SUPER ADMIN LOGIN (full page, not popup)
════════════════════════════════════════ */
export default function SuperAdminLogin({platform, onSuccess, onBack, lang, setLang}){
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const t=PT[lang];
  const isRTL=lang==="ar";
  const accent=platform.platformAccent||"#C9A84C";
  const theme=usePlatformThemeMode(platform);
  const C=themeColors(theme);

  const [submitting,setSubmitting]=useState(false);
  const submit=async()=>{
    setSubmitting(true);setErr("");
    try{
      const r=await authFetch("/api/auth",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({action:"login_sa",email:email.trim(),password:pw})});
      const d=await r.json();
      if(r.ok&&d.ok){if(d.token)setAuthToken(d.token);setSubmitting(false);onSuccess();}
      else{setErr(d.error||t.incorrectPw);setSubmitting(false);}
    }catch{
      setErr("Network error — please try again.");
      setSubmitting(false);
    }
  };

  const bodyBg=theme==="dark"?"#08080f":"#FAFAF9";
  if(theme==="dark"){
    const accentCss=buildDarkAccentCss(accent);
    return(
      <div className={`p-dark${isRTL?" rtl":""}`} style={{fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"'Inter',-apple-system,sans-serif"}}>
        <style>{`\nbody{background:${bodyBg};overflow-x:hidden;}`}</style>
        {accentCss&&<style>{accentCss}</style>}
        <div className="p-dark-blob"/>
        <nav className="p-nav-dark">
          <div className="p-logo-dark">
            {platform.platformLogo
              ?<img src={platform.platformLogo} style={{height:28,objectFit:"contain"}} alt={platform.platformName}/>
              :<span>{(platform.platformName||"Command").split(" ")[0]}{(platform.platformName||"Command").split(" ").length>1&&<span className="p-logo-dark-accent"> {(platform.platformName||"Command").split(" ").slice(1).join(" ")}</span>}</span>
            }
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <div style={{display:"flex",gap:3,marginRight:6}}>
              <button className={`p-lang-btn-dark ${lang==="en"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("en")}>EN</button>
              <button className={`p-lang-btn-dark ${lang==="ar"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("ar")}>ع</button>
            </div>
            <button className="p-btn-ghost-dark" onClick={onBack}>{t.back}</button>
          </div>
        </nav>
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"120px 24px 24px",position:"relative",zIndex:1}}>
          <div style={{maxWidth:440,width:"100%"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
              <h2 style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:24,fontStyle:"italic",fontWeight:300,color:"white"}}>{t.saLogin}</h2>
              <span style={{fontSize:10,background:accent+"20",color:accent,padding:"2px 9px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace",fontWeight:600,border:"1px solid "+accent+"40"}}>{t.saTitle}</span>
            </div>
            <p style={{fontSize:14,color:"rgba(255,255,255,.42)",marginBottom:24}}>{platform.platformName}</p>
            {err&&<div className="p-err-dark" style={{marginBottom:14}}>{err}</div>}
            <div className="p-fg-dark"><label className="p-lbl-dark">{t.email}</label><input className="p-inp-dark" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={platform.superAdminEmail} autoFocus onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
            <div className="p-fg-dark"><label className="p-lbl-dark">{t.password}</label><input className="p-inp-dark" type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
            <button className="p-btn-primary-dark" onClick={submit} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:10,marginTop:8}}>{t.saAccess} →</button>
          </div>
        </div>
      </div>
    );
  }
  const accentCssLight=buildLightAccentCss(accent);
  return(
    <div className={`p-light${isRTL?" rtl":""}`} style={{minHeight:"100vh",fontFamily:isRTL?"'Tajawal',-apple-system,sans-serif":"'Inter',-apple-system,sans-serif"}}>
      <style>{`\nbody{background:${bodyBg};overflow-x:hidden;}`}</style>
      {accentCssLight&&<style>{accentCssLight}</style>}
      <nav className="p-nav-light">
        <div className="p-logo-light">
          {platform.platformLogo
            ?<img src={platform.platformLogo} style={{height:28,objectFit:"contain"}} alt={platform.platformName}/>
            :<span>{(platform.platformName||"Command").split(" ")[0]}{(platform.platformName||"Command").split(" ").length>1&&<span className="p-logo-light-accent"> {(platform.platformName||"Command").split(" ").slice(1).join(" ")}</span>}</span>
          }
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <button className={`p-lang-btn-light ${lang==="en"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("en")}>EN</button>
          <button className={`p-lang-btn-light ${lang==="ar"?"active":""}`} style={{padding:"8px 10px",fontSize:13}} onClick={()=>setLang("ar")}>ع</button>
          <button className="p-btn-ghost-light" onClick={onBack}>{t.back}</button>
        </div>
      </nav>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:"120px 24px 24px",position:"relative",zIndex:1}}>
        <div style={{maxWidth:440,width:"100%"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <h2 style={{fontFamily:"'Fraunces',Georgia,serif",fontSize:24,fontStyle:"italic",fontWeight:300,color:"var(--cink,#1A1714)"}}>{t.saLogin}</h2>
            <span style={{fontSize:10,background:accent+"20",color:accent,padding:"2px 9px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace",fontWeight:600,border:"1px solid "+accent+"40"}}>{t.saTitle}</span>
          </div>
          <p style={{fontSize:14,color:"var(--cg500,#6B6660)",marginBottom:24}}>{platform.platformName}</p>
          {err&&<div className="p-err-light" style={{marginBottom:14}}>{err}</div>}
          <div className="p-fg-light"><label className="p-lbl-light">{t.email}</label><input className="p-inp-light" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={platform.superAdminEmail} autoFocus onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
          <div className="p-fg-light"><label className="p-lbl-light">{t.password}</label><input className="p-inp-light" type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="••••••••" onKeyDown={e=>e.key==="Enter"&&submit()}/></div>
          <button className="p-btn-primary-light" onClick={submit} style={{width:"100%",padding:"14px",fontSize:15,borderRadius:10,marginTop:8}}>{t.saAccess} →</button>
        </div>
      </div>
    </div>
  );
}
